import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Database, Download, Inbox, Info, Loader2, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import CommonTooltip from "../common/CommonTooltip";
import AlertModal from "../common/AlertModal";
import PageSizeSelector from "../common/PageSizeSelector";
import PagerArrowButton from "../common/PagerArrowButton";
import { toast } from "../../utils/toast";
import { isRTL } from "../../utils/rtl";
import { fetchProtectionJobs } from "../../service/Security";
import { downloadSkippedAccountsExcel } from "../../utils/skippedAccountsExport";
import { apiErrorMessage } from "../../utils/apiError";
import { AddToMasterDbModal } from "./AddToMasterDbModal";

export const PROTECTION_JOBS_ROUTE = "/security/protection-jobs";

// While any job on the page is IN_PROGRESS, the page is refreshed every 4 seconds;
// once every job is COMPLETED or FAILED nothing is polled.
const POLL_INTERVAL_MS = 4000;
const isActiveJob = (job) => job?.status === "IN_PROGRESS";

// The skip reason the backend gives for accounts that are not in the Master DB yet.
const isMissingFromMasterDb = (item) => /master\s*db/i.test(String(item?.reason ?? ""));
const missingMasterDbAccounts = (job) =>
  (job?.skippedAccounts || []).filter(isMissingFromMasterDb).map((item) => String(item.accountNo));

const STATUS_STYLES = {
  QUEUED: { badge: "bg-slate-100 text-slate-600 border-slate-200", bar: "bg-slate-400", Icon: Clock },
  IN_PROGRESS: { badge: "bg-blue-50 text-blue-700 border-blue-200", bar: "bg-[#236DB4]", Icon: Loader2 },
  COMPLETED: { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "bg-emerald-500", Icon: CheckCircle2 },
  FAILED: { badge: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500", Icon: XCircle },
};

const clampPercent = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(Math.max(number, 0), 100) : 0;
};

// Round progress ring, shown only while a job is running.
const ProgressRing = ({ percent }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 36 36"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={percent}
    className="shrink-0"
  >
    <circle cx="18" cy="18" r="15.9155" fill="none" strokeWidth="4" className="stroke-slate-200" />
    <circle
      cx="18"
      cy="18"
      r="15.9155"
      fill="none"
      strokeWidth="4"
      strokeLinecap="round"
      strokeDasharray={`${percent}, 100`}
      transform="rotate(-90 18 18)"
      className="stroke-[#236DB4] transition-[stroke-dasharray] duration-500"
    />
  </svg>
);


export const ProtectionJobs = () => {
  const { projectId, theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const isRtl = isRTL(langMode);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [masterDbJob, setMasterDbJob] = useState(null);
  const [failedJob, setFailedJob] = useState(null);
  // jobId -> last seen status, so a job that finishes while this screen is open can be announced.
  const lastStatusRef = useRef(new Map());

  const downloadSkipped = useCallback((job) => {
    downloadSkippedAccountsExcel(job.skippedAccounts, `skipped-accounts-job-${job.jobId}`);
  }, []);

  const announceFinishedJobs = useCallback(
    (nextJobs) => {
      nextJobs.forEach((job) => {
        const previous = lastStatusRef.current.get(job.jobId);
        const wasActive = previous === "QUEUED" || previous === "IN_PROGRESS";
        if (wasActive && job.status === "COMPLETED") {
          const message = t.protectionJobFinishedSummary
            .replace("{protected}", job.protectedCount ?? 0)
            .replace("{skipped}", job.skippedCount ?? 0);
          if ((job.skippedAccounts || []).length > 0) {
            toast.warning(message);
          } else {
            toast.success(message);
          }
        } else if (wasActive && job.status === "FAILED") {
          toast.error(job.errorMessage || t.protectionJobFailedToast);
        }
        lastStatusRef.current.set(job.jobId, job.status);
      });
    },
    [t]
  );

  const loadJobs = useCallback(
    async ({ silent = false } = {}) => {
      if (!projectId) {
        setJobs([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const result = await fetchProtectionJobs(projectId, { page, size: pageSize });
        const sorted = [...result.jobs].sort((a, b) => Number(b.jobId) - Number(a.jobId));
        announceFinishedJobs(sorted);
        setJobs(sorted);
        setTotalCount(result.totalCount);
        setLoadFailed(false);
      } catch (error) {
        // A failed background refresh keeps the jobs already on screen.
        if (!silent) {
          setJobs([]);
          setTotalCount(0);
          setLoadFailed(true);
          toast.error(apiErrorMessage(error?.response?.data) || t.protectionJobsLoadFailed);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [projectId, page, pageSize, announceFinishedJobs, t]
  );

  // A new project, page or page size starts from a clean "last seen" map and a full reload.
  useEffect(() => {
    lastStatusRef.current = new Map();
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [projectId]);

  const hasActiveJob = useMemo(() => jobs.some(isActiveJob), [jobs]);

  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const timer = window.setInterval(() => loadJobs({ silent: true }), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasActiveJob, loadJobs]);

  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
  const startIndex = jobs.length === 0 ? 0 : page * pageSize + 1;
  const endIndex = page * pageSize + jobs.length;
  const hasNextPage = page < totalPages - 1;

  const formatDateTime = (value) => {
    if (!value) return t.notAvailable;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t.notAvailable;
    return date.toLocaleString(langMode === "ar" ? "ar" : langMode === "ur" ? "ur" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const headers = [
    t.protectionJobColSrNo,
    t.protectionJobColStatus,
    t.protectionJobTotal,
    t.protectionJobProtected,
    t.protectionJobSkipped,
    t.protectionJobColSkippedFile,
    t.protectionJobCreated,
    t.protectionJobFinished,
    t.protectionJobColActions,
  ];

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden ${
        theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
      }`}
    >
      <div className="mx-2 mt-2 shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:px-5 sm:py-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/security")}
            aria-label={t.protectionJobsBack}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#236DB4] hover:bg-blue-100"
          >
            <ArrowLeft size={18} className={isRtl ? "rotate-180" : ""} />
          </button>
          <div className="min-w-0 flex-1 basis-52">
            <h1 className="text-base font-bold leading-tight text-slate-800 sm:text-lg">{t.protectionJobsTitle}</h1>
            <p className="mt-0.5 text-xs text-slate-500">{t.protectionJobsSubtitle}</p>
          </div>
        </div>
        {hasActiveJob && (
          <p
            role="status"
            data-testid="protection-jobs-running-note"
            className="mt-3 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-[#236DB4]"
          >
            <Info size={14} className="mt-0.5 shrink-0" />
            <span className="min-w-0">{t.protectionJobsAutoRefresh}</span>
          </p>
        )}
      </div>

      <div className="mx-2 mb-3 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4">
        <div className="flex min-h-[56px] shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
          <p className="text-xs text-slate-400">
            {startIndex}-{endIndex} {t.of} {totalCount}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500">
            <span>{t.rowsPerPage}</span>
            <PageSizeSelector
              value={pageSize}
              onChange={(value) => {
                setPage(0);
                setPageSize(value);
              }}
              height="32px"
              fontSize="12px"
            />
            <PagerArrowButton
              direction="prev"
              isRtl={isRtl}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
            />
            <span className="min-w-[38px] text-center tabular-nums text-slate-600">
              {page + 1} / {totalPages}
            </span>
            <PagerArrowButton
              direction="next"
              isRtl={isRtl}
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!hasNextPage}
            />
            <CommonButton
              colorIntent="primary"
              variant="primary"
              onClick={() => loadJobs({ silent: true })}
              disabled={loading || refreshing}
              wrapperClassName="shrink-0"
              className="!h-8 !w-8 sm:!w-auto !p-0 sm:!px-3 !justify-center"
              data-testid="protection-jobs-refresh"
            >
              <RefreshCw size={13} className={refreshing || loading ? "animate-spin" : "shrink-0"} />
              <span className="hidden whitespace-nowrap sm:inline">{t.protectionJobsRefresh}</span>
            </CommonButton>
          </div>
        </div>

        <div className="relative min-h-[240px] w-full flex-1">
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-7 w-7 animate-spin text-[#236DB4]" />
                {t.Processing}
              </div>
            </div>
          )}
          <div className="absolute inset-0 overflow-auto">
            <table className="w-full min-w-[960px] table-auto text-start text-xs text-slate-600">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                <tr>
                  {headers.map((label) => (
                    <th key={label} className="whitespace-nowrap px-4 py-3 text-start">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={headers.length} className="py-16 align-middle">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                          <Inbox size={22} />
                        </div>
                        <div className="text-sm font-semibold text-slate-800">
                          {loadFailed ? t.protectionJobsLoadFailed : t.protectionJobsEmpty}
                        </div>
                        {!loadFailed && <div className="mt-1 max-w-xs text-xs text-slate-400">{t.protectionJobsEmptyHint}</div>}
                      </div>
                    </td>
                  </tr>
                ) : (
                  jobs.map((job, index) => {
                    const style = STATUS_STYLES[job.status] || STATUS_STYLES.QUEUED;
                    const StatusIcon = style.Icon;
                    const percent = clampPercent(job.progressPercent);
                    const isCompleted = job.status === "COMPLETED";
                    const skippedCount = isCompleted ? (job.skippedAccounts || []).length : 0;
                    const masterDbAccounts = isCompleted ? missingMasterDbAccounts(job) : [];
                    // The flags only matter when there are skipped accounts; with none, no button is shown.
                    // addedWithProtection: nothing left to do. addedToMasterDb: only protection is left.
                    const hasMasterDbAccounts = masterDbAccounts.length > 0;
                    const masterDbDone = hasMasterDbAccounts && Boolean(job.addedWithProtection);
                    const masterDbTooltip = masterDbDone
                      ? t.protectionJobMasterDbDoneTooltip
                      : hasMasterDbAccounts && job.addedToMasterDb
                        ? t.protectionJobMasterDbAddedTooltip
                        : t.protectionJobMasterDbTooltip;
                    const inMasterDb = hasMasterDbAccounts && Boolean(job.addedToMasterDb);
                    const masterDbLabel = inMasterDb ? t.protectionJobAddProtection : t.protectionJobAddMasterDb;
                    const MasterDbIcon = inMasterDb ? ShieldCheck : Database;
                    return (
                      <tr
                        key={job.jobId}
                        data-testid={`protection-job-${job.jobId}`}
                        className="border-b border-slate-100 align-middle hover:bg-slate-50/60"
                      >
                        <td className="px-4 py-3 tabular-nums text-slate-500">{page * pageSize + index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {job.status === "FAILED" ? (
                              <CommonTooltip
                                text={t.reportFailedTooltip}
                                position="top"
                                disableDefaultDescription
                                tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                                fitContent
                                clampToViewport
                              >
                                <button
                                  type="button"
                                  onClick={() => setFailedJob(job)}
                                  aria-haspopup="dialog"
                                  aria-label={t.reportFailedTooltip}
                                  data-testid={`failed-status-${job.jobId}`}
                                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 ${style.badge}`}
                                >
                                  <StatusIcon size={13} />
                                  {t.protectionJobStatusFAILED}
                                </button>
                              </CommonTooltip>
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${style.badge}`}>
                                <StatusIcon size={13} className={job.status === "IN_PROGRESS" ? "animate-spin" : ""} />
                                {t[`protectionJobStatus${job.status}`] || job.status}
                              </span>
                            )}
                            {job.status === "IN_PROGRESS" && (
                              <span className="inline-flex items-center gap-1.5">
                                <ProgressRing percent={percent} />
                                <span className="font-semibold tabular-nums text-slate-700">{percent}%</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 tabular-nums">{job.totalCount ?? t.notAvailable}</td>
                        <td className="px-4 py-3 tabular-nums">{job.protectedCount ?? t.notAvailable}</td>
                        <td className="px-4 py-3 tabular-nums">{job.skippedCount ?? t.notAvailable}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {skippedCount > 0 ? (
                            <CommonTooltip
                              text={t.protectionJobDownloadSkippedTooltip}
                              position="top"
                              disableDefaultDescription
                              tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                              fitContent
                              clampToViewport
                              hoverOnly
                            >
                              <button
                                type="button"
                                onClick={() => downloadSkipped(job)}
                                aria-label={t.protectionJobDownloadSkippedTooltip}
                                data-testid={`download-skipped-${job.jobId}`}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#1d5a96]"
                              >
                                <Download size={13} />
                                {t.download}
                              </button>
                            </CommonTooltip>
                          ) : (
                            <span className="text-slate-300">{t.notAvailable}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(job.createdAt)}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(job.finishedAt)}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {hasMasterDbAccounts ? (
                            <CommonTooltip
                              text={masterDbTooltip}
                              position="top"
                              disableDefaultDescription
                              tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                              fitContent
                              clampToViewport
                              hoverOnly
                            >
                              <CommonButton
                                colorIntent="success"
                                variant="primary"
                                onClick={() => !masterDbDone && setMasterDbJob(job)}
                                disabled={masterDbDone}
                                aria-label={masterDbLabel}
                                data-testid={`add-master-db-${job.jobId}`}
                                className="!h-auto !min-h-0 !px-3 !py-1.5 !text-xs !gap-1.5 focus:!ring-0 focus:!shadow-none"
                              >
                                <MasterDbIcon size={13} />
                                {masterDbLabel}
                              </CommonButton>
                            </CommonTooltip>
                          ) : (
                            <span className="text-slate-300">{t.notAvailable}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {createPortal(
        <AlertModal
          open={Boolean(failedJob)}
          variant="error"
          title={t.protectionJobFailedTitle}
          message={
            <span className="block max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
              {failedJob?.errorMessage || t.protectionJobFailedToast}
            </span>
          }
          showCloseIcon={false}
          onClose={() => setFailedJob(null)}
          dataTestId="protection-job-error-alert"
        />,
        document.body
      )}

      <AddToMasterDbModal
        open={Boolean(masterDbJob)}
        onClose={() => setMasterDbJob(null)}
        jobId={masterDbJob?.jobId}
        alreadyInMasterDb={Boolean(masterDbJob?.addedToMasterDb)}
        accountNos={masterDbJob ? missingMasterDbAccounts(masterDbJob) : []}
        onDone={({ protectionStarted }) => {
          // A protection request adds a new job (page 0, newest first); otherwise just refresh.
          if (protectionStarted && page !== 0) setPage(0);
          else loadJobs({ silent: true });
        }}
      />
    </div>
  );
};

export default ProtectionJobs;
